// Turns the pure Import files screen state into words. Pure given a translate function, so
// the copy (button, stats line, badges, descriptions) is covered by Jest against the real
// English messages. Ported from Android's `ImportTexts.kt` / `ImportDisplay.kt`; the
// `import_files.*` keys carry the same copy as Android's `import_*` strings.
//
// Keys are spelled out in full at every call: lib/i18n_client_bundle.rb ships only the keys a
// literal t('…') call names, so a key built at runtime would never reach the browser.

import type { ImportError, ImportIntent, SkipReason } from './importTypes';
import {
  choiceDestination,
  IMPORT_DESTINATIONS,
  countOf,
  type ImportChoice,
  type ImportCounts,
  type ImportCta,
  type ImportDestination,
  type ImportFileMeta
} from './importScreenState';
import type { ImportFailure } from './importSave';

/** vue-i18n's `t`: (key), (key, named), (key, plural) or (key, named, plural). */
export type Translate = (key: string, ...args: any[]) => string;

/** A recording's length on its row: "4h 52m", or "52m" under an hour. Blank when unknown. */
export function formatImportDuration(durationMs: number): string {
  if (durationMs <= 0) return '';
  const totalMinutes = Math.floor(durationMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours}h ${String(minutes).padStart(2, '0')}m` : `${minutes}m`;
}

/** "a", "a and b", "a, b and c" — `and` joins the last pair (the localised "{first} and {last}"). */
export function joinWithAnd(parts: string[], and: (first: string, last: string) => string): string {
  if (!parts.length) return '';
  if (parts.length === 1) return parts[0];
  return and(parts.slice(0, -1).join(', '), parts[parts.length - 1]);
}

/** The non-zero counts for the button and the result, in recordings → hikes → groups order. */
export const nonZero = (c: ImportCounts): Array<[ImportDestination, number]> =>
  IMPORT_DESTINATIONS.map(d => [d, countOf(c, d)] as [ImportDestination, number]).filter(([, n]) => n > 0);

export function destinationName(t: Translate, d: ImportDestination): string {
  if (d === 'recordings') return t('import_files.dest_recordings');
  if (d === 'plannedHikes') return t('import_files.dest_planned');
  return t('import_files.dest_waypoints');
}

/** "To Recordings · 3 files"; just "3 files" with no start screen. */
export function subtitle(t: Translate, home: ImportDestination | null, fileCount: number): string {
  const files = t('import_files.files_count', fileCount);
  if (home === 'recordings') return t('import_files.subtitle_to_recordings', { files });
  if (home === 'plannedHikes') return t('import_files.subtitle_to_planned', { files });
  if (home === 'waypoints') return t('import_files.subtitle_to_waypoints', { files });
  return files;
}

function countPhrase(t: Translate, d: ImportDestination, n: number): string {
  if (d === 'recordings') return t('import_files.count_recordings', n);
  if (d === 'plannedHikes') return t('import_files.count_planned', n);
  return t('import_files.count_waypoint_groups', n);
}

/** "10 recordings, 12 planned hikes and 2 waypoint groups". */
export function countsList(t: Translate, c: ImportCounts): string {
  return joinWithAnd(
    nonZero(c).map(([d, n]) => countPhrase(t, d, n)),
    (first, last) => t('import_files.list_pair', { first, last })
  );
}

export function ctaLabel(t: Translate, cta: ImportCta): string {
  if (cta.kind === 'openInPlanner') return t('import_files.cta_open_in_planner');
  if (cta.kind === 'importThenPlanner') return t('import_files.cta_import_then_planner', { list: countsList(t, cta.counts) });
  if (cta.kind === 'nothing') return t('import_files.cta_nothing');
  return t('import_files.cta_import', { list: countsList(t, cta.counts) });
}

/** "Imported 3 recordings and 1 planned hike, 2 not imported". */
export function resultText(t: Translate, c: ImportCounts, notImported: number): string {
  const imported = t('import_files.result_imported', { list: countsList(t, c) });
  return notImported > 0 ? `${imported}, ${t('import_files.result_not_imported', notImported)}` : imported;
}

/** Why a file wasn't imported, in words; null when the result just names it. */
export function failureReason(t: Translate, f: ImportFailure): string | null {
  if (f.reason === 'noRoute') return t('import_files.failed_no_route');
  return null;
}

/** The failed files on the result: "a.gpx, b.gpx (Couldn't plan a route between the waypoints)". */
export function failedFilesText(t: Translate, failures: ImportFailure[]): string {
  return failures
    .map(f => {
      const reason = failureReason(t, f);
      return reason ? `${f.name} (${reason})` : f.name;
    })
    .join(', ');
}

export function tileLabel(t: Translate, d: ImportDestination, n: number): string {
  if (d === 'recordings') return t('import_files.tile_recordings', n);
  if (d === 'plannedHikes') return t('import_files.tile_planned', n);
  return t('import_files.tile_waypoint_groups', n);
}

export function skipReasonText(t: Translate, reason: SkipReason | null, intent: ImportIntent): string {
  switch (reason) {
    case 'no_times':
      return t('import_files.skip_no_times');
    case 'generated_times':
      return t('import_files.skip_generated_times');
    case 'partial_times':
      return t('import_files.skip_partial_times');
    case 'only_waypoints':
      return intent === 'recording' ? t('import_files.skip_only_waypoints_track') : t('import_files.skip_only_waypoints_line');
    case 'no_waypoints':
      return t('import_files.skip_no_waypoints');
    default:
      return t('import_files.meta_not_imported');
  }
}

export function errorTitle(t: Translate, error: ImportError): string {
  if (error === 'not_readable') return t('import_files.error_not_readable_title');
  if (error === 'empty') return t('import_files.error_empty_title');
  return t('import_files.error_unsupported_title');
}

/** What's wrong, likely cause, what to do. */
export function errorBlocks(t: Translate, error: ImportError): { what: string; why: string; fix: string } {
  if (error === 'not_readable') {
    return {
      what: t('import_files.error_not_readable_what'),
      why: t('import_files.error_not_readable_why'),
      fix: t('import_files.error_not_readable_fix')
    };
  }
  if (error === 'empty') {
    return { what: t('import_files.error_empty_what'), why: t('import_files.error_empty_why'), fix: t('import_files.error_empty_fix') };
  }
  return {
    what: t('import_files.error_unsupported_what'),
    why: t('import_files.error_unsupported_why'),
    fix: t('import_files.error_unsupported_fix')
  };
}

/** The badge above a file's name: what it becomes. Null while reading, unreadable or left out. */
export function typeBadge(t: Translate, m: ImportFileMeta): string | null {
  switch (m.kind) {
    case 'recording':
      return t('import_files.option_recording');
    case 'planned':
      return m.editInPlanner ? t('import_files.option_edit_in_planner') : t('import_files.option_planned');
    case 'waypoints':
      return t('import_files.dest_waypoints');
    case 'fromWaypoints':
      return t('import_files.option_planned');
    default:
      return null;
  }
}

/** The row's stats line, e.g. "3 days · 59.7 km · 3 highlights"; the type is in `typeBadge`. */
export function metaText(t: Translate, m: ImportFileMeta, formatDistance: (meters: number) => string): string {
  let parts: string[];
  switch (m.kind) {
    case 'reading':
      parts = [t('import_files.meta_reading')];
      break;
    case 'unreadable':
      parts = [errorTitle(t, m.error)];
      break;
    case 'skipped':
      parts = [skipReasonText(t, m.reason, m.intent)];
      break;
    case 'recording':
      parts = [formatDistance(m.distanceM), formatImportDuration(m.durationMs)];
      break;
    case 'planned':
      parts = [
        m.days > 1 ? t('import_files.meta_days', m.days) : '',
        formatDistance(m.distanceM),
        m.highlights > 0 ? t('import_files.meta_highlights', m.highlights) : ''
      ];
      break;
    case 'waypoints':
      parts = [t('import_files.meta_waypoints', m.count)];
      break;
    case 'fromWaypoints':
      if (m.route === 'failed') {
        parts = [t('import_files.failed_no_route')];
      } else {
        parts = [
          m.route === 'routing' ? t('import_files.meta_planning_route') : formatDistance(m.distanceM),
          t('import_files.meta_through_waypoints', m.count),
          m.keptAsHighlights ? t('import_files.meta_kept_as_highlights') : ''
        ];
      }
      break;
  }
  return parts.filter(p => p.trim() !== '').join(' · ');
}

/** How a row's stats line is coloured. */
export type ImportMetaTone = 'normal' | 'warn' | 'error';

export function metaTone(m: ImportFileMeta): ImportMetaTone {
  if (m.kind === 'unreadable') return 'error';
  if (m.kind === 'fromWaypoints' && m.route === 'failed') return 'error';
  if (m.kind === 'skipped' && m.needsLook) return 'warn';
  return 'normal';
}

export function optionName(t: Translate, choice: ImportChoice): string {
  switch (choice) {
    case 'planned':
      return t('import_files.option_planned');
    case 'editInPlanner':
      return t('import_files.option_edit_in_planner');
    case 'recording':
      return t('import_files.option_recording');
    case 'waypoints':
      return t('import_files.dest_waypoints');
    case 'fromWaypoints':
      return t('import_files.option_from_waypoints');
    default:
      return t('import_files.option_skip');
  }
}

/** An option's one-line description, or where it goes when that isn't the start screen. */
export function optionDescription(
  t: Translate,
  choice: ImportChoice,
  waypointCount: number,
  home: ImportDestination | null,
  elsewhere: boolean
): string {
  if (elsewhere && home) {
    const homeName = destinationName(t, home);
    if (choice === 'fromWaypoints') {
      return t('import_files.option_from_waypoints_elsewhere', { count: waypointCount, home: homeName }, waypointCount);
    }
    const goesTo = choiceDestination(choice);
    if (goesTo) return t('import_files.option_goes_elsewhere', { destination: destinationName(t, goesTo), home: homeName });
  }
  switch (choice) {
    case 'planned':
      return t('import_files.option_saves_to', { destination: destinationName(t, 'plannedHikes') });
    case 'editInPlanner':
      return t('import_files.option_edit_desc');
    case 'recording':
      return t('import_files.option_saves_to', { destination: destinationName(t, 'recordings') });
    case 'waypoints':
      return t('import_files.option_waypoints_desc');
    case 'fromWaypoints':
      return t('import_files.option_from_waypoints_desc', waypointCount);
    default:
      return t('import_files.option_skip_desc');
  }
}

/** "2 need a look · 1 can't be read". */
export function lookBarLabel(t: Translate, lookCount: number, errorCount: number): string {
  return [
    lookCount > 0 ? t('import_files.bar_need_look', lookCount) : '',
    errorCount > 0 ? t('import_files.bar_cant_read', errorCount) : ''
  ]
    .filter(Boolean)
    .join(' · ');
}

/** "Not imported", or "2 added" once files behind the bar are chosen. */
export const lookBarRight = (t: Translate, addedCount: number): string =>
  addedCount > 0 ? t('import_files.bar_added', addedCount) : t('import_files.meta_not_imported');

/** A file row's or option's icon (a Material Icons name) and how it's tinted. */
export interface ImportIcon {
  name: string;
  tone: 'white' | 'grey' | 'red';
}

/** White on a dark circle, except Don't import (grey) and an unreadable file (red). */
export function importIcon(choice: ImportChoice | null, error: boolean): ImportIcon {
  if (error) return { name: 'error_outline', tone: 'red' };
  switch (choice) {
    case 'recording':
      return { name: 'radio_button_checked', tone: 'white' };
    case 'waypoints':
      return { name: 'place', tone: 'white' };
    case 'editInPlanner':
      return { name: 'edit', tone: 'white' };
    case 'planned':
    case 'fromWaypoints':
      return { name: 'route', tone: 'white' };
    default:
      return { name: 'description', tone: 'grey' };
  }
}
